import { Col, Container, Modal, Row } from 'react-bootstrap'
import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { toast } from 'react-toastify'
import { FacebookShareButton, TwitterShareButton, WhatsappShareButton } from 'react-share'
import FooterNew from '../components/Footer'
import Navigation from '../components/Navigation'
import { useStore } from '../../app/stores/store'
import PostImagesSwiper from '../components/PostImagesSwiper'
import AuctionCountDown from '../components/auction/AuctionCountDown'
import AuctionBidInput from '../components/auction/AuctionBidInput'
import AuctionBidCard from '../components/auction/AuctionBidCard'
import AuctionSolvencyAttachment from '../components/auction/AuctionSolvencyAttachment'
import GoogleMapComp from '../components/GoogleMapComp'
import { history } from '../..'
import DeletePopup from '../components/DeletePopup'
import { AuctionPublic, FilterModes } from '../../app/models/modes'
import UnauthorizedBidder from '../components/auction/UnauthorizedBidder'
import QueuePosition from '../components/auction/QueuePosition'
import AuctionEdit from './AuctionEdit'
import AuctionBuyNow from '../components/auction/AuctionBuyNow'
import { currencyValue, formatCurrency } from '../components/muiStyle/muiStyle'
import ComponentLoader from '../components/loaders/ComponentLoader'
import AuctionReview from '../components/auction/AuctionReview'
import { PostImage } from '../../app/models/Post'
import DeletedItemModal from '../components/DeletedItemModal'
import { AuctionStatus } from '../../app/models/Auction'
import Advertisment from '../components/advertisment/Advertisment'
import CustomBar from '../components/custombar/CustomBar'

function AuctionDetails() {
  const { bidsStore, userStore, auctionStore, methodsStore, favoriteAuctionsStore } = useStore()
  const { auctionDetails, loadingInitial } = bidsStore
  const { selectedCurrency } = methodsStore
  const { addFavorite, removeFavorite, findFavorite } = favoriteAuctionsStore
  const {
    showDelete,
    setShowDelete,
    showEditAuction,
    closeShowEditAuction,
    setReviewAuction,
    reviewAuction,
  } = methodsStore
  const [hasAuctionFinished, setHasAuctionFinished] = useState(false)
  const { id } = useParams()
  const { t } = useTranslation()
  const [loadingBtn, setLoadingBtn] = useState(false)
  const location = useLocation()
  const completed = new Date() > new Date(auctionDetails?.deadline || '')

  useEffect(() => {
    return () => {
      bidsStore.clearActionDetails()
    }
  }, [])

  useEffect(() => {
    if (id && !bidsStore.auctionDetails) {
      bidsStore.loadAuctionDetails(id).catch(() => {
        // redirect to home page if auction is not allowed to be viewed

        toast.error(t('auction_not_viewable'))

        history.push('/login?redirectTo=' + id)
      })
    }
  }, [bidsStore.auctionDetails])

  useEffect(() => {
    // private auctions cannot be viewed by non logged in users
    if (!userStore.user && auctionDetails?.public === AuctionPublic.Private) {
      history.push('/login')
    }
    if (id && auctionDetails) {
      bidsStore.createHubConnection(auctionDetails.id)
      return () => {
        bidsStore.clearBids()
      }
    }
  }, [bidsStore, id, userStore.user, auctionDetails])

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = new Date().getTime()
      const deadlineTime = new Date(auctionDetails?.deadline as Date).getTime()

      if (now >= deadlineTime) {
        setHasAuctionFinished(true)
        clearInterval(intervalId)
      } else {
        setHasAuctionFinished(false)
      }
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [hasAuctionFinished, auctionDetails?.deadline, id])

  useEffect(() => {
    bidsStore.updateCurrentBidWithCurrency()
  }, [bidsStore])

  if (loadingInitial) {
    return <ComponentLoader mTop={100} />
  }

  function handleDelete() {
    setLoadingBtn(true)
    auctionStore
      .deleteAuction(id!)
      .then(() => {
        history.push('/auctions')
      })
      .catch((err) => console.log(err))
      .finally(() => {
        setShowDelete(false)
        setLoadingBtn(false)
      })
  }

  const goToPost = () => {
    history.push(`/posts/${auctionDetails?.post?.slug}`)
  }

  const goBack = () => {
    history.push('/auctions')
  }

  const handleAddFav = () => {
    if (!userStore.isLoggedIn) {
      toast.error(t('err_login'))
    } else {
      setLoadingBtn(true)
      addFavorite(id!, FilterModes.auction).finally(() => setLoadingBtn(false))
    }
  }

  const handleRemoveFav = () => {
    setLoadingBtn(true)
    removeFavorite(id!, auctionDetails?.post?.title || '', FilterModes.auction).finally(() =>
      setLoadingBtn(false),
    )
  }

  const favButtons = [
    {
      icon: 'fa-regular fa-heart',
      onClick: handleAddFav,
      show: !findFavorite(id!),
    },
    {
      icon: 'fa-solid fa-heart',
      onClick: handleRemoveFav,
      show: findFavorite(id!),
    },
  ]

  const currencyName = currencyValue.find(
    (item) => item.value === auctionDetails?.post?.currency,
  )?.name

  const fullUrl = window.location.origin + location.pathname + location.search + location.hash

  const auctionDesc = () => (
    <>
      <div className='post-details-header mt-0'>
        <h1>{auctionDetails?.post?.title}</h1>
        <div>
          <h6>{t('share_opts')}: </h6>
          <div className='share-options'>
            <FacebookShareButton resetButtonStyle={false} url={fullUrl}>
              <i className='fa-brands fa-facebook-f' />
            </FacebookShareButton>
            <TwitterShareButton
              resetButtonStyle={false}
              url={fullUrl}
              title={auctionDetails?.post?.title}
            >
              <i className='fa-brands fa-twitter' />
            </TwitterShareButton>
            <WhatsappShareButton resetButtonStyle={false} url={fullUrl}>
              <i className='fa-brands fa-whatsapp' />
            </WhatsappShareButton>
          </div>
        </div>
      </div>
      <hr className='auction-card-line' />
      <Advertisment />
      <hr className='auction-card-line' />
      <CustomBar />
      <hr className='auction-card-line' />
      <Container className='post-details-main-info ms-0'>
        <Row className='align-items-end'>
          <Col md={5}>
            <h2>{t('main_info')}</h2>
            <p>{t('room')}</p>
            <p>{t('availability')}</p>
            <p>{t('category')}</p>
            {auctionDetails?.post?.userCompanyName && <p>Company Name</p>}
          </Col>
          <Col className='fw-bold'>
            <p>{auctionDetails?.post?.rooms}</p>
            <p>{auctionDetails?.post?.meters} m²</p>
            <p>{t(auctionDetails?.post?.category as string)}</p>
            {auctionDetails?.post?.userCompanyName && (
              <p>
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href={auctionDetails.post.userCompanyUrl}
                >
                  {auctionDetails.post.userCompanyName}
                </a>
              </p>
            )}
          </Col>
        </Row>
      </Container>
      <hr className='auction-card-line' />
      <div className='auction-details-map'>
        <p>
          {auctionDetails?.post?.address}, {auctionDetails?.post?.city}{' '}
          {auctionDetails?.post?.zipCode}
        </p>
        <GoogleMapComp
          address={auctionDetails?.post?.address as string}
          zipCode={auctionDetails?.post?.zipCode as number}
          city={auctionDetails?.post?.city as string}
          height={525}
        />
      </div>
    </>
  )

  return (
    <>
      <Navigation auctionStatus={auctionDetails?.status} type={FilterModes.auction} />

      <Container fluid className='post-details-container'>
        <div className='auction-details'>
          <div className='post-details-navigation mb-4'>
            <p onClick={goBack}>← {t('go_back')}</p>
            <p onClick={goToPost} className='text-end'>
              {t('view_post')} →
            </p>
          </div>
          <Row>
            <Col lg={6}>
              <div className='auction-details-image mb-5'>
                <PostImagesSwiper
                  postImages={auctionDetails?.post?.postImages as PostImage[]}
                  details
                  favButtons={favButtons}
                  loadingFavBtn={loadingBtn}
                  slug={auctionDetails?.post?.slug || ''}
                  id={auctionDetails?.id as string}
                  round={auctionDetails?.roundNumber}
                  totalRounds={auctionDetails?.totalRounds}
                  auctionStatus={auctionDetails?.status}
                />
              </div>
              <div className='auction-details-desc'>
                {!hasAuctionFinished && <AuctionBuyNow />}
                {auctionDesc()}
              </div>
            </Col>
            <Col lg={6}>
              <div className='auction-details-desc-responsive'>
                {!hasAuctionFinished && <AuctionBuyNow />}
              </div>

              <AuctionSolvencyAttachment />

              <div className='auction-details-prices'>
                <h1>
                  {auctionDetails?.status === 4 ? (
                    <p className='currencies'>
                      {t('final_price')}:
                      <strong className='ms-2'>
                        {`${currencyName} ${formatCurrency(auctionDetails.post?.price)}`}
                      </strong>
                    </p>
                  ) : (
                    <p className='currencies'>
                      {t('current_bid')}:
                      <strong className='ms-2'>
                        {`${currencyName} ${formatCurrency(bidsStore.currentBid)}`}
                      </strong>
                    </p>
                  )}

                  {bidsStore.currentBid !== 0 && selectedCurrency !== currencyName && (
                    <p className='currencies converted-bid'>
                      {t('auction_page.converted_bid')}
                      <strong className='ms-2'>
                        {`${selectedCurrency} ${formatCurrency(bidsStore.convertedBid)}`}
                      </strong>
                    </p>
                  )}
                </h1>
              </div>

              <div className='auction-details-timer'>
                <p>{t('time_left')}:</p>
                <AuctionCountDown
                  startDate={auctionDetails?.startingDate as Date}
                  endDate={auctionDetails?.deadline as Date}
                  bidding
                />
              </div>
              <div className='d-flex justify-content-between'>
                {!auctionDetails?.bids && (
                  <p className='p-0 my-2'>
                    {t('min_price')}:
                    {` ${formatCurrency(auctionDetails?.minPrice)} ${currencyName}`}
                  </p>
                )}
                {auctionDetails?.bids && (
                  <p className='p-0 my-2'>
                    {t('min_incr_price', {
                      price: auctionDetails?.minIncreasePrice,
                      currency: currencyName,
                    })}
                  </p>
                )}
              </div>
              {!hasAuctionFinished && <AuctionBidInput />}

              <br />
              <hr className='auction-card-line' />
              <AuctionBidCard />
            </Col>
          </Row>
          <Row className='auction-details-desc-responsive'>
            <hr className='auction-card-line mt-4 mb-5' />
            <Col lg={6}>{auctionDesc()}</Col>
          </Row>
          <Row>
            <Col lg={9}>
              <hr className='auction-card-line' />
              <Container className='post-details-main-info ms-0'>
                <Row className='align-items-end'>
                  <Col md={10}>
                    <h2>{t('desc')}</h2>
                    <p>{auctionDetails?.post?.description}</p>
                  </Col>
                </Row>
              </Container>
              <hr className='auction-card-line' />
              <Container className='post-details-main-info ms-0'>
                <Row className='align-items-end'>
                  <Col md={4}>
                    <h2>{t('ameneties')}</h2>
                    {auctionDetails?.post?.tags.map((tagObject, index) => (
                      <div
                        key={index}
                        className='d-flex justify-content-between align-items-center'
                      >
                        <p>{t(`auction_tag.${tagObject.tag.toLowerCase()}`)}</p>
                        <p>
                          <i className='fa-solid fa-check' />
                        </p>
                      </div>
                    ))}
                  </Col>
                </Row>
              </Container>
              {methodsStore.canReviewAuction() && (hasAuctionFinished || completed) && (
                <>
                  <hr className='auction-card-line' />
                  <Container className='post-details-main-info ms-0'>
                    <button
                      onClick={() => setReviewAuction(true)}
                      className='post-details-contact-btn d-block'
                    >
                      {t('review_auction')}
                    </button>
                  </Container>
                </>
              )}
            </Col>
          </Row>
        </div>
      </Container>
      <FooterNew />

      {/* Auction Delete Modal */}
      <Modal size='lg' centered show={showDelete} onHide={() => setShowDelete(false)}>
        <DeletePopup
          handleClose={() => setShowDelete(false)}
          handleDelete={handleDelete}
          loadingBtn={loadingBtn}
          title={t('auction')}
        />
      </Modal>

      {/* Auction Edit Modal */}
      <Modal size='lg' centered show={showEditAuction} onHide={() => closeShowEditAuction()}>
        <AuctionEdit handleClose={() => closeShowEditAuction()} />
      </Modal>

      <Modal size='lg' centered show={reviewAuction} onHide={() => setReviewAuction(false)}>
        <AuctionReview handleClose={() => setReviewAuction(false)} />
      </Modal>

      {/* Unauthorized Bidder Modal */}
      <UnauthorizedBidder />
      {/* Queue Position Modal */}
      <QueuePosition />
      {auctionDetails?.status === AuctionStatus.Deleted && !methodsStore.isAuctionCreator() && (
        <DeletedItemModal type={FilterModes.auction} />
      )}
    </>
  )
}
export default observer(AuctionDetails)
